import { useEffect, useState, type ReactNode } from 'react'
import { User, KeyRound } from 'lucide-react'
import { useT } from '@/i18n'
import { ApiError } from '@/lib/api'
import { RelativeTime } from '@/components/DataDisplay'
import { Skeleton } from '@/components/States'
import { fetchBotOwner, fetchBotToken, type BotOwner, type BotTokenInfo } from './botApi'

// Only the sections backed by real server data survive here: owner (BotAggregate.OwnerUserId)
// and token status (BotAggregate.Token, prefix only). Everything else this file used to export
// (webhook, mini-apps, business connections, affiliate, games, reports, stats, app-settings,
// preview-media) had no backing data in the actual MyTelegram backend - removed rather than left
// as permanently-404-hidden dead code.

function SectionCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="mt-3 max-w-xl rounded-lg border border-border-default bg-surface-2 p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-secondary">
        {icon}
        {title}
      </div>
      {children}
    </div>
  )
}

function isNotDeployed(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404
}

export function BotOwnerSection({ botId }: { botId: number }) {
  const t = useT()
  const [loading, setLoading] = useState(true)
  const [owner, setOwner] = useState<BotOwner | null>(null)
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setHidden(false)
    fetchBotOwner(botId, ac.signal)
      .then((o) => {
        if (alive) setOwner(o)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        if (isNotDeployed(e)) setHidden(true)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [botId])

  if (hidden) return null
  const icon = <User size={14} />
  if (loading)
    return (
      <SectionCard icon={icon} title={t.bots.ownerTitle}>
        <Skeleton className="h-4 w-1/2" />
      </SectionCard>
    )
  if (!owner)
    return (
      <SectionCard icon={icon} title={t.bots.ownerTitle}>
        <span className="text-sm text-tertiary">{t.bots.noOwner}</span>
      </SectionCard>
    )
  return (
    <SectionCard icon={icon} title={t.bots.ownerTitle}>
      <div className="space-y-0.5 text-sm">
        <div className="text-primary">
          {owner.first_name || (owner.username ? `@${owner.username}` : `#${owner.id}`)}
        </div>
        {owner.username && <div className="text-secondary">@{owner.username}</div>}
        {owner.last_seen != null && (
          <div className="text-tertiary">
            {t.users.lastSeen}: <RelativeTime value={owner.last_seen} />
          </div>
        )}
      </div>
    </SectionCard>
  )
}

function useBotSection<T>(
  load: (id: number, signal: AbortSignal) => Promise<T>,
  botId: number,
): { loading: boolean; data: T | null; hidden: boolean } {
  const [state, setState] = useState<{ loading: boolean; data: T | null; hidden: boolean }>({
    loading: true,
    data: null,
    hidden: false,
  })
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setState({ loading: true, data: null, hidden: false })
    load(botId, ac.signal)
      .then((d) => {
        if (alive) setState({ loading: false, data: d, hidden: false })
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setState({ loading: false, data: null, hidden: isNotDeployed(e) })
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [botId, load])
  return state
}

export function BotTokenSection({ botId }: { botId: number }) {
  const t = useT()
  const { loading, data, hidden } = useBotSection<BotTokenInfo | null>(fetchBotToken, botId)
  if (hidden) return null
  const icon = <KeyRound size={14} />
  if (loading)
    return (
      <SectionCard icon={icon} title={t.bots.tokenTitle}>
        <Skeleton className="h-4 w-1/2" />
      </SectionCard>
    )
  if (!data)
    return (
      <SectionCard icon={icon} title={t.bots.tokenTitle}>
        <span className="text-sm text-tertiary">{t.bots.noToken}</span>
      </SectionCard>
    )
  return (
    <SectionCard icon={icon} title={t.bots.tokenTitle}>
      <div className="space-y-0.5 text-sm">
        <div className="flex items-center gap-2">
          <code className="rounded bg-surface-1 px-1.5 py-0.5 font-mono text-xs text-secondary">
            {data.token_prefix ? `${data.token_prefix}••••••` : '—'}
          </code>
          {data.revoked ? (
            <span className="text-xs font-medium text-danger">{t.bots.tokenRevoked}</span>
          ) : (
            <span className="text-xs font-medium text-accent">{t.bots.tokenActive}</span>
          )}
        </div>
        {data.issued_at != null && (
          <div className="text-tertiary">
            {t.bots.tokenIssued}: <RelativeTime value={data.issued_at} />
          </div>
        )}
        {data.rotated_at != null && (
          <div className="text-tertiary">
            {t.bots.tokenRotated}: <RelativeTime value={data.rotated_at} />
          </div>
        )}
        {data.revoked && data.revoked_at != null && (
          <div className="text-danger">
            {t.bots.tokenRevokedAt}: <RelativeTime value={data.revoked_at} />
          </div>
        )}
      </div>
    </SectionCard>
  )
}
